// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import set from 'lodash/set';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import startsWith from 'lodash/startsWith';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import pick from 'lodash/pick';
import pipe from 'lodash/fp/pipe';
import replace from 'lodash/fp/replace';
import { getLogger } from '@nerdwallet/logging';

import appConfig from '@nerdwallet/app-config';

import { nwFetch } from '../client/lib/fetch';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { receiveOutline } from '../client/containers/builder/actions';
import baseLandingPageConfig from './base-landing-page-config';
import log from '../client/lib/log';

const logger = getLogger('landing-page-client', log);
const CONFIG_CACHE_EXPIRATION = 60 * 60 * 24 * 30 * 6; // ~6 mo

/**
 * Extracts the page config slug from an inbound req path.
 *
 * @param {string} path - the requested path
 *
 * @returns {string}
 *
 */
export const getSlug = pipe(
  replace('/l/', ''),
  replace('builder/', ''),
  // remove the starting slash
  replace(/^\//, ''),
  // WP doesn't support slashes in the slug, replace them with dashes
  replace(/\//g, '-')
);

/**
 * Sets the config and dynamicOpts on the res object.
 *
 * @param {object} config - the landing page config
 * @param {object} res - the res object to mutate
 *
 * @returns {undefined}
 *
 */
export const writeConfigToRes = (config, res) => {
  // Put it in the locals
  set(res, 'locals.routeConfig', config);

  // Does it need the GlobalNav
  const needsGlobalNav = get(config, 'meta.needsGlobalNav', false);

  if (!needsGlobalNav) {
    set(res, 'locals.markupOpts', ['start', 'markup', 'footer', 'end']);
  }

  const customHeaders = get(config, 'meta.customHeaders');

  if (Array.isArray(customHeaders)) {
    customHeaders.forEach(([name, value]) => res.setHeader(name, value));
  }
};

/**
 * These params are used on requests to WordPress for previews of draft or fork
 * content. We use this list to recognize such requests, so we can avoid caching them.
 *
 * @enum {string}
 */
export const wpPreviewParams = [
  '_wpnonce',
  'preview',
  'fork_nonce',
  'fork_id',
  'fork_preview',
];

/**
 * Generates fetch options for retrieving a config for a given path.
 * If development, will source from /server/dev-configs/ if available.
 * Isolated for ease of testing.
 *
 * @param {object} options
 *  @param {object} options[path] - the path of the page to fetch
 *  @param {string} options[query] - the query params
 *  @param {object} options[cache] - the cache commands object
 *  @param {object} options[cacheExpiration] - time to cache in seconds
 *  @param {boolean} options[bypassCache] - whether or not to get the cached value
 *  @param {object} options[cookies] - the user's cookies
 *
 * @returns {object}
 *
 */
export const getConfigFetchOptions = ({
  data = {},
  query = {},
  cache,
  rawCacheClient,
  cacheExpiration = CONFIG_CACHE_EXPIRATION,
  bypassCache,
  cookies,
}) => {
  const path = get(data, 'path');
  const slug = getSlug(path);
  const url = `${appConfig.API.BASE_URLS.WP_API}/nw/v1/landing-pages/${slug}/`;
  const isPreview = wpPreviewParams.some((param) => query[param]);

  return {
    slug,
    url,
    cache,
    rawCacheClient,
    cacheExpiration,
    bypassCache: isPreview || bypassCache, // Don't read cache for previews or refreshes
    bypassCacheWrite: isPreview, // Don't write previews to cache
    logger,
    cookies,
    headers: {
      // If we are fetching a config, we want the latest, not the cached version
      'x-bypass-cache': 'bypass',
    },
    params: {
      ...pick(query, wpPreviewParams),
      client_id: 'landing-page-client',
    },
  };
};

/**
 * Sources a config based on options.
 * If development, will source from /server/dev-configs/ if available.
 * Uses slug for logic; other options are passed to nwFetch. See docs for that function.
 *
 * @param {object} options
 *  @param {object} options[slug] - the slug of the config
 *
 * @returns {Promise}
 *
 */
export const sourceConfig = ({ slug, ...fetchOptions }) => {
  if (slug === '') {
    return Promise.reject(Error('No valid page slug provided'));
  }

  // Render a blank state.
  if (slug === 'new') {
    return Promise.resolve(baseLandingPageConfig);
  }

  // Source the config from local file if available.
  if (
    !process.env.IN_BROWSER &&
    ['staging', 'production'].indexOf(process.env.CONFIG_ENV) === -1
  ) {
    // Require fs here. Only needed at runtime, don't try to bundle for client
    const fs = require('fs'); // eslint-disable-line global-require

    const developmentRoute = `./server/dev-configs/${slug}.json`;

    if (fs.existsSync(developmentRoute)) {
      const localConfig = fs.readFileSync(developmentRoute, 'utf8');
      let parsedConfig;

      try {
        parsedConfig = JSON.parse(localConfig);
      } catch (err) {
        return Promise.reject(
          new Error(`Error parsing JSON for ${developmentRoute}. ${err}`)
        );
      }

      logger.info('Using development config route', developmentRoute);

      return Promise.resolve(parsedConfig);
    }
  }

  // Fetch the config from elsewhere.
  return nwFetch(fetchOptions).then((response) => {
    /* eslint-disable camelcase */
    const { id, nw_content_data, nw_seo } = response;
    return { contentId: id, ...nw_content_data, nw_seo };
    /* eslint-enable camelcase */
  });
};

/**
 * Fetches a config for a given req.
 * Sets config and dynamic opts on res object.
 *
 * @param {object} req - the req object
 * @param {object} res - the res object
 * @param {function} next - express next callback
 *
 * @returns {Promise}
 */
export const configFetcherExpressMiddleware = (req, res, next) => {
  // Don't attempt to fetch config for public asset URLs.
  if (startsWith(req.path, appConfig.ASSETS)) {
    return next();
  }

  const fetchOptions = getConfigFetchOptions({
    data: {
      path: req.path,
    },
    // commands gives us access to get and set with some bonus error handling
    cache: get(req, 'cache.commands'),
    // client gives us the raw redis client, which we need for anything not set/get
    rawCacheClient: get(req, 'cache.client'),
    query: get(req, 'query'),
    cookies: req.cookies,
  });

  return sourceConfig(fetchOptions)
    .then((config) => {
      writeConfigToRes(config, res);
    })
    .then(() => next())
    .catch((err) => {
      // Don't blow up, but alert as to failed route fetch
      req.log.error(`Error fetching route data for ${req.path}`);
      req.log.error(get(err, 'message'));
      next();
    });
};

/**
 * Fetches a config for a given path.
 * Returns a promise that resolves with the OUTLINE_RECEIVED action.
 * Only fetches landing pages that are published in WP.
 *
 * @param {object} options
 *  @param {object} options[path] - the path of the page to fetch. Can include wp nonce
 *  @param {object} options[cache] - the cache commands object
 *  @param {boolean} options[bypassCache] - whether or not to get the cached value
 *  @param {object} options[logger] - the logging object
 *
 * @returns {Promise}
 *
 */
export const fetchConfig = (params) =>
  sourceConfig(getConfigFetchOptions(params)).then(receiveOutline);

export default configFetcherExpressMiddleware;
